import React, { useCallback } from 'react';

import { useFormContext, useWatch } from 'react-hook-form';

import { RadioGroup, RadioGroupItem } from 'components/ui/radio-group';

import {
  EOfferFormatKey,
  ETimeAbbreviationStyle,
  IStoreSettingsFormValues
} from '../../../StoreSettings.types';
import {
  ABBREVIATION_RADIO_CLASSNAME,
  ABBREVIATIONS_V2_TEST_IDS,
  ABBREVIATIONS_V2_TEXTS,
  TIME_ABBREVIATION_STYLE_OPTIONS,
  getTimeAbbreviationStyleTestId
} from '../AbbreviationsSettingsV2.consts';
import {
  SettingsFieldPath,
  SettingsFieldValue
} from '../AbbreviationsSettingsV2.types';
import { getTimeFieldPath } from '../AbbreviationsSettingsV2.utils';

import { SettingsTile } from './SettingsTile';

interface IAbbreviationStyleFieldProps {
  sectionKey: EOfferFormatKey;
  onCommit: (path: SettingsFieldPath, value: SettingsFieldValue) => void;
}

export const AbbreviationStyleField = ({
  sectionKey,
  onCommit
}: IAbbreviationStyleFieldProps): React.ReactElement => {
  const { control } = useFormContext<IStoreSettingsFormValues>();
  const stylePath = getTimeFieldPath(sectionKey, 'abbreviationStyle');
  const abbreviationStyle = useWatch({ control, name: stylePath });

  const handleStyleChange = useCallback(
    (value: string): void => {
      onCommit(stylePath, value as ETimeAbbreviationStyle);
    },
    [onCommit, stylePath]
  );

  return (
    <SettingsTile
      title={ABBREVIATIONS_V2_TEXTS.ABBREVIATION_STYLE_LABEL}
      tooltip={ABBREVIATIONS_V2_TEXTS.ABBREVIATION_STYLE_TOOLTIP}
      testId={ABBREVIATIONS_V2_TEST_IDS.abbreviationStyleTile}
    >
      <RadioGroup
        value={String(abbreviationStyle)}
        onValueChange={handleStyleChange}
        className="flex flex-col gap-3"
      >
        {TIME_ABBREVIATION_STYLE_OPTIONS.map((option) => (
          <label
            key={option.value}
            className="flex cursor-pointer items-center gap-2"
          >
            <RadioGroupItem
              value={option.value}
              className={ABBREVIATION_RADIO_CLASSNAME}
              data-testid={getTimeAbbreviationStyleTestId(option.value)}
            />
            <span className="text-sm font-normal text-base-mutedForeground">
              {option.label}
            </span>
          </label>
        ))}
      </RadioGroup>
    </SettingsTile>
  );
};
